import {
  KeyboardEvent,
  ReactNode,
  RefCallback,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MoreHorizontal } from "lucide-react";
import { Popover } from "../Popover/Popover";
import styles from "./Menu.module.css";

export interface MenuItem {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  variant?: "default" | "danger";
  disabled?: boolean;
  /**
   * A word at the item's right edge, in the faint grey of something that is
   * not a control — the reason it cannot be pressed, said where the pressing
   * would have happened.
   *
   * It exists because a whole menu can be greyed out by one condition the
   * operator cannot see from here: while a project is being wiped, Suspend,
   * Wipe and Delete are all refused, and an operator left to guess why
   * "Delete project" stopped working will reload the page to find out.
   * Deliberately not a tooltip — a disabled item takes no pointer events, so
   * the explanation has to be on screen already.
   */
  hint?: string;
}
export interface MenuSeparator {
  separator: true;
}

type Entry = MenuItem | MenuSeparator;

interface Props {
  items: Entry[];
  /**
   * Optional trigger override. If omitted, the component renders a default ⋯
   * kebab button that opens itself on click.
   */
  trigger?: (api: {
    open: boolean;
    toggle: () => void;
    /**
     * Attach this to the trigger element — the popover anchors to it and focus
     * returns to it on close. It is a callback ref, not an object: a render
     * prop that received the object could read `.current` mid-render, which is
     * exactly what Rules of React forbids.
     */
    ref: RefCallback<HTMLButtonElement>;
  }) => ReactNode;
  /** Optional placement passed through to Popover */
  placement?: "bottom-start" | "bottom-end";
  "aria-label"?: string;
}

function isSeparator(entry: Entry): entry is MenuSeparator {
  return (entry as MenuSeparator).separator === true;
}

export function Menu({
  items,
  trigger,
  placement = "bottom-end",
  "aria-label": ariaLabel,
}: Props) {
  const [open, setOpen] = useState(false);
  // The trigger element is state, not a ref: the render prop below hands the
  // setter out as its callback ref, and a setter — unlike a ref object — cannot
  // be read back during render.
  const [triggerEl, setTriggerEl] = useState<HTMLButtonElement | null>(null);
  const anchorRef = useMemo(() => ({ current: triggerEl }), [triggerEl]);

  const toggle = useCallback(() => setOpen((o) => !o), []);
  const close = useCallback(() => setOpen(false), []);

  const dismiss = useCallback(() => {
    close();
    triggerEl?.focus();
  }, [close, triggerEl]);

  const triggerNode = trigger ? (
    trigger({ open, toggle, ref: setTriggerEl })
  ) : (
    <button
      ref={setTriggerEl}
      type="button"
      className={styles.kebab}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label={ariaLabel ?? "More actions"}
      onClick={toggle}
    >
      <MoreHorizontal width={14} height={14} aria-hidden="true" />
    </button>
  );

  return (
    <>
      {triggerNode}
      <Popover
        open={open}
        onClose={close}
        anchorRef={anchorRef}
        placement={placement}
      >
        <MenuList items={items} ariaLabel={ariaLabel} onDismiss={dismiss} />
      </Popover>
    </>
  );
}

/**
 * The open menu's keyboard state. It lives here rather than in `Menu` because
 * the panel only exists while the menu is open: the highlight starts on the
 * first selectable item every time, without an effect that resets it.
 */
function MenuList({
  items,
  ariaLabel,
  onDismiss,
}: {
  items: Entry[];
  ariaLabel?: string;
  onDismiss: () => void;
}) {
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  /**
   * Every index the keyboard may land on — which is every item, refused or
   * not, and only separators are skipped.
   *
   * Refused items keep their place in the roving focus because they are
   * `aria-disabled` rather than natively `disabled` (below). Walking past them
   * is the more obvious rule and it is the one that breaks: a whole menu can
   * be refused by a single condition — while a project is being wiped all
   * three of its items are — and then this list is empty, `activeIndex` is -1,
   * nothing inside the panel is focused, the `keydown` handler bound to
   * `role="menu"` never receives a key, and the `hint` that explains why the
   * menu went dark is on screen for the mouse and in nobody's ears. Skipping
   * refused items only works while something is not refused.
   */
  const focusableIndices = items
    .map((it, idx) => ({ it, idx }))
    .filter(({ it }) => !isSeparator(it))
    .map(({ idx }) => idx);

  const [activeIndex, setActiveIndex] = useState<number>(
    () => focusableIndices[0] ?? -1,
  );

  // Focus the active item when it changes.
  useEffect(() => {
    if (activeIndex < 0) return;
    const el = itemRefs.current[activeIndex];
    if (el) {
      el.focus();
      // Scroll into view in case of long menus.
      if (typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeIndex]);

  function moveActive(direction: 1 | -1) {
    if (focusableIndices.length === 0) return;
    const currentPos = focusableIndices.indexOf(activeIndex);
    let nextPos: number;
    if (currentPos === -1) {
      nextPos = direction === 1 ? 0 : focusableIndices.length - 1;
    } else {
      nextPos =
        (currentPos + direction + focusableIndices.length) %
        focusableIndices.length;
    }
    setActiveIndex(focusableIndices[nextPos]);
  }

  function handleListKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveActive(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onDismiss();
    } else if (e.key === "Home") {
      e.preventDefault();
      if (focusableIndices.length > 0) setActiveIndex(focusableIndices[0]);
    } else if (e.key === "End") {
      e.preventDefault();
      if (focusableIndices.length > 0)
        setActiveIndex(focusableIndices[focusableIndices.length - 1]);
    }
  }

  // The one place a refused item is actually refused, now that the attribute
  // no longer does it: reachable and readable, but pressing it neither calls
  // back nor closes the panel — a menu that shut itself would read as an
  // action accepted.
  function select(item: MenuItem) {
    if (item.disabled) return;
    item.onSelect();
    onDismiss();
  }

  return (
    <div
      role="menu"
      aria-label={ariaLabel}
      className={styles.list}
      onKeyDown={handleListKeyDown}
    >
      {items.map((entry, idx) => {
        if (isSeparator(entry)) {
          return (
            <hr key={`sep-${idx}`} className={styles.sep} aria-hidden="true" />
          );
        }
        const item = entry;
        const isActive = idx === activeIndex;
        const isDanger = item.variant === "danger";
        return (
          <button
            key={`item-${idx}-${item.label}`}
            ref={(el) => {
              itemRefs.current[idx] = el;
            }}
            type="button"
            role="menuitem"
            aria-selected={isActive}
            // `aria-disabled`, never the attribute: a natively disabled button
            // is out of the focus order and out of reach, and the words that
            // say WHY it is refused ride on it. Refusal is enforced in
            // `select` below instead — the APG's arrangement for menus, and
            // the only one under which a menu that is entirely refused can
            // still be read.
            aria-disabled={item.disabled || undefined}
            tabIndex={isActive ? 0 : -1}
            className={`${styles.item} ${isDanger ? styles.danger : ""} ${
              item.disabled ? styles.disabled : ""
            }`}
            onClick={() => select(item)}
            onMouseEnter={() => {
              if (!item.disabled) setActiveIndex(idx);
            }}
          >
            {item.icon && <span className={styles.icon}>{item.icon}</span>}
            <span className={styles.label}>{item.label}</span>
            {item.hint && <span className={styles.hint}>{item.hint}</span>}
          </button>
        );
      })}
    </div>
  );
}
